import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Trash2, Tag, Loader2, X } from 'lucide-react'
import { adminApi, AdminRepresentative, CreateDiscountCodeRequest } from '../../services/api'

const DISCOUNT_TYPES = [
  { value: 'FreeMonths', label: 'Darmowe miesiące' },
  { value: 'Percentage', label: 'Procent zniżki' },
  { value: 'FixedAmount', label: 'Stała kwota (PLN)' },
]

const DURATION_KINDS = [
  { value: 'FirstMonthOnly', label: 'Tylko pierwszy miesiąc' },
  { value: 'NMonths', label: 'N miesięcy' },
  { value: 'EntireSubscription', label: 'Cała subskrypcja' },
]

export default function DiscountCodesPage() {
  const queryClient = useQueryClient()
  const [showDialog, setShowDialog] = useState(false)
  const [filterActive, setFilterActive] = useState<boolean | undefined>(undefined)

  const { data, isLoading } = useQuery({
    queryKey: ['admin-discount-codes', filterActive],
    queryFn: () => adminApi.listDiscountCodes({ isActive: filterActive, pageSize: 100 }).then(r => r.data),
  })

  const { data: repsData } = useQuery({
    queryKey: ['admin-representatives-list'],
    queryFn: () => adminApi.listRepresentatives({ pageSize: 100 }).then(r => r.data),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminApi.deleteDiscountCode(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-discount-codes'] }),
  })

  const codes = data?.items || []
  const reps = repsData?.items || []

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-gray-900">Kody rabatowe</h2>
        <button onClick={() => setShowDialog(true)} className="btn-primary py-2 px-4 flex items-center gap-2">
          <Plus className="w-4 h-4" />
          Nowy kod
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-2 mb-4">
        <button
          onClick={() => setFilterActive(undefined)}
          className={`px-3 py-1.5 rounded-lg text-sm ${filterActive === undefined ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}
        >
          Wszystkie
        </button>
        <button
          onClick={() => setFilterActive(true)}
          className={`px-3 py-1.5 rounded-lg text-sm ${filterActive === true ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}
        >
          Aktywne
        </button>
        <button
          onClick={() => setFilterActive(false)}
          className={`px-3 py-1.5 rounded-lg text-sm ${filterActive === false ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'}`}
        >
          Nieaktywne
        </button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Kod</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Typ</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Wartość</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Czas trwania</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Wygasa</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Użycia</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Przedstawiciel</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Akcje</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {codes.map((code) => (
                <tr key={code.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Tag className="w-4 h-4 text-blue-500" />
                      <span className="font-mono font-medium text-sm">{code.code}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {DISCOUNT_TYPES.find(t => t.value === code.discountType)?.label || code.discountType}
                  </td>
                  <td className="px-4 py-3 text-sm font-medium">
                    {code.discountType === 'Percentage' ? `${code.discountValue}%` :
                     code.discountType === 'FixedAmount' ? `${code.discountValue} PLN` :
                     `${code.discountValue} mies.`}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {DURATION_KINDS.find(d => d.value === code.durationKind)?.label || code.durationKind}
                    {code.durationMonths ? ` (${code.durationMonths})` : ''}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {code.expiresAt ? new Date(code.expiresAt).toLocaleDateString('pl-PL') : '—'}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {code.timesUsed}{code.maxUses != null ? ` / ${code.maxUses}` : ''}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {code.representativeName || '—'}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                      code.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                    }`}>
                      {code.isActive ? 'Aktywny' : 'Nieaktywny'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => deleteMutation.mutate(code.id)}
                      disabled={!code.isActive}
                      className="text-red-400 hover:text-red-600 disabled:opacity-30"
                      title="Dezaktywuj"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
              {codes.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-4 py-8 text-center text-gray-400">
                    Brak kodów rabatowych
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {showDialog && (
        <CreateCodeDialog
          representatives={reps}
          onClose={() => setShowDialog(false)}
          onCreated={() => {
            setShowDialog(false)
            queryClient.invalidateQueries({ queryKey: ['admin-discount-codes'] })
          }}
        />
      )}

    </div>
  )
}

function CreateCodeDialog({
  representatives,
  onClose,
  onCreated,
}: {
  representatives: AdminRepresentative[]
  onClose: () => void
  onCreated: () => void
}) {
  const [form, setForm] = useState<CreateDiscountCodeRequest>({
    code: '',
    discountType: 'Percentage',
    discountValue: 0,
    durationKind: 'FirstMonthOnly',
  })
  const [error, setError] = useState<string | null>(null)

  const mutation = useMutation({
    mutationFn: (data: CreateDiscountCodeRequest) => adminApi.createDiscountCode(data),
    onSuccess: () => onCreated(),
    onError: (err: Error & { response?: { data?: { error?: string } } }) => {
      setError(err.response?.data?.error || 'Nie udało się utworzyć kodu.')
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.code.trim()) {
      setError('Kod jest wymagany.')
      return
    }
    mutation.mutate(form)
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-900">Nowy kod rabatowy</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Kod *</label>
            <input
              type="text"
              value={form.code}
              onChange={e => setForm({ ...form, code: e.target.value.toUpperCase() })}
              placeholder="np. RABAT50"
              className="input w-full font-mono"
              maxLength={50}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Typ rabatu *</label>
              <select
                value={form.discountType}
                onChange={e => setForm({ ...form, discountType: e.target.value })}
                className="input w-full"
              >
                {DISCOUNT_TYPES.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Wartość *</label>
              <input
                type="number"
                step="0.01"
                value={form.discountValue}
                onChange={e => setForm({ ...form, discountValue: parseFloat(e.target.value) || 0 })}
                className="input w-full"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Czas trwania</label>
              <select
                value={form.durationKind}
                onChange={e => setForm({ ...form, durationKind: e.target.value })}
                className="input w-full"
              >
                {DURATION_KINDS.map(d => (
                  <option key={d.value} value={d.value}>{d.label}</option>
                ))}
              </select>
            </div>
            {form.durationKind === 'NMonths' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Liczba miesięcy</label>
                <input
                  type="number"
                  value={form.durationMonths || ''}
                  onChange={e => setForm({ ...form, durationMonths: parseInt(e.target.value) || undefined })}
                  className="input w-full"
                  min={1}
                />
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Data wygaśnięcia</label>
              <input
                type="date"
                value={form.expiresAt ? form.expiresAt.split('T')[0] : ''}
                onChange={e => setForm({ ...form, expiresAt: e.target.value ? new Date(e.target.value).toISOString() : undefined })}
                className="input w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Max użyć</label>
              <input
                type="number"
                value={form.maxUses || ''}
                onChange={e => setForm({ ...form, maxUses: parseInt(e.target.value) || undefined })}
                className="input w-full"
                min={1}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Przedstawiciel</label>
            <select
              value={form.representativeId || ''}
              onChange={e => setForm({ ...form, representativeId: e.target.value || undefined })}
              className="input w-full"
            >
              <option value="">— brak —</option>
              {representatives.map(r => (
                <option key={r.id} value={r.id}>{r.name} ({r.email})</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notatki</label>
            <textarea
              value={form.notes || ''}
              onChange={e => setForm({ ...form, notes: e.target.value || undefined })}
              className="input w-full"
              rows={2}
            />
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary py-2 px-4">
              Anuluj
            </button>
            <button type="submit" disabled={mutation.isPending} className="btn-primary py-2 px-4 flex items-center gap-2">
              {mutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
              Utwórz kod
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
